import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken } from '../Services/services';
import { createOrder } from '../api/orders';
import { purchaseCart } from '../api/inventory';
import { uploadFile } from '../api/files';
import { track, logger } from '../utils/logger';
import AppHeader from './AppHeader';

export default function Checkout() {
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    mode = 'quote',
    quoteId,
    quoteLineIds,
    quoteLines,
    cartItemIds,
    cartItems,
  } = location.state || {};

  const isCartMode = mode === 'cart';

  const [poNumber, setPoNumber] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const isGuest = !getSessionToken();

  const summaryLines = isCartMode
    ? (cartItems || []).map((i) => ({
      Id: i.id,
      ProductName: i.part_number,
      Condition: i.condition,
      Quantity: i.quantity_requested,
      Price: i.price,
    }))
    : (quoteLines || []);

  const lineCount = isCartMode
    ? (cartItemIds?.length || 0)
    : (quoteLineIds?.length || 0);

  useEffect(() => {
    if (isGuest) {
      openLogin({ redirectPath: isCartMode ? '/cart' : '/my-rfqs' });
      return;
    }
    if (isCartMode) {
      if (!cartItemIds?.length) navigate('/cart', { replace: true });
      return;
    }
    if (!quoteId || !quoteLineIds?.length) {
      navigate('/my-rfqs', { replace: true });
    }
  }, [isGuest, isCartMode, quoteId, quoteLineIds, cartItemIds, openLogin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!poNumber.trim()) {
      setError('PO Number is required.');
      return;
    }

    setSubmitting(true);
    setError('');
    track('order.submit', {
      lineCount,
      hasAttachment: !!file,
      mode: isCartMode ? 'cart' : 'quote',
    });

    try {
      let orderId;
      let orderName;

      if (isCartMode) {
        const result = await purchaseCart(cartItemIds, poNumber.trim());
        orderId = result?.order_id || result?.orderId;
        orderName = result?.order_name || result?.Name || orderId;
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        const orderLines = quoteLineIds.map((lineId) => ({
          inscor__Customer_Quote_Line__c: lineId,
        }));

        const result = await createOrder({
          inscor__Customer_Quote__c: quoteId,
          inscor__Customer_PO_Number__c: poNumber.trim(),
          orderLines,
        });

        orderId = result?.orderId || result?.Id;
        orderName = result?.Name || result?.orderName || orderId;
      }

      if (file && orderId) {
        try {
          const reader = new FileReader();
          const base64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          await uploadFile(orderId, {
            Title: file.name,
            PathOnClient: file.name,
            VersionData: base64,
          });
        } catch (uploadErr) {
          logger.warn('order.attachment_failed', { hasOrderId: !!orderId, message: uploadErr?.message });
        }
      }

      track('order.placed', { hasOrderId: !!orderId, lineCount, mode: isCartMode ? 'cart' : 'quote' });
      setSuccess({ orderId, orderName });
    } catch (err) {
      logger.error('order.submit_failed', { lineCount, status: err?.status, message: err?.message });
      setError(err?.message || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (isGuest || (!isCartMode && !quoteId) || (isCartMode && !cartItemIds?.length)) return null;

  if (success) {
    return (
      <div style={styles.page}>
        <AppHeader title="Order Confirmed" />
        <main style={styles.main}>
          <div style={styles.container}>
            <div style={styles.card}>
              <div style={styles.successIcon}>✓</div>
              <h2 style={styles.successTitle}>Purchase Complete</h2>
              <p style={styles.successDetail}>
                Sales Order <strong>{success.orderName}</strong> has been created.
              </p>
              <div style={styles.successActions}>
                <button
                  type="button"
                  style={{ ...styles.primaryBtn, backgroundColor: branding.primaryColor }}
                  onClick={() => navigate('/my-orders')}
                >
                  View My Orders
                </button>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={() => navigate(isCartMode ? '/cart' : '/my-rfqs')}
                >
                  {isCartMode ? 'Back to Cart' : 'Back to Quotes'}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <AppHeader title="Checkout" />

      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.noticeBanner}>
            We currently support <strong>invoicing only</strong>. No credit card or online payment is required.
            Please provide your Purchase Order number below.
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Order Summary</h3>
            <p style={styles.muted}>
              {lineCount} line{lineCount !== 1 ? 's' : ''}
              {isCartMode ? ' from Shopping Cart' : ' from quote'}
            </p>
            {summaryLines.length > 0 && (
              <div style={styles.lineTableWrap}>
                <table style={styles.lineTable}>
                  <thead>
                    <tr>
                      <th style={styles.lineTh}>Part</th>
                      <th style={styles.lineTh}>Condition</th>
                      <th style={styles.lineTh}>Qty</th>
                      <th style={styles.lineTh}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryLines.map((l, idx) => (
                      <tr key={l.Id || idx} style={idx % 2 === 0 ? styles.lineRowEven : undefined}>
                        <td style={styles.lineTd}>
                          {l.ProductName || l.inscor__Product_Name__c || l.inscor__Part_Number__c || '—'}
                        </td>
                        <td style={styles.lineTd}>
                          {l.Condition || l.inscor__Condition__c || '—'}
                        </td>
                        <td style={styles.lineTd}>
                          {l.Quantity ?? l.inscor__Quantity__c ?? '—'}
                        </td>
                        <td style={styles.lineTd}>
                          {l.Price != null || l.inscor__Price__c != null
                            ? `$${Number(l.Price ?? l.inscor__Price__c).toFixed(2)}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} style={styles.card}>
            <h3 style={styles.sectionTitle}>Purchase Details</h3>

            <div style={styles.field}>
              <label style={styles.label} htmlFor="poNumber">
                PO Number <span style={styles.required}>*</span>
              </label>
              <input
                id="poNumber"
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder="Enter your Purchase Order number"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label} htmlFor="paperwork">
                Supporting Paperwork <span style={styles.optional}>(optional)</span>
              </label>
              <input
                id="paperwork"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={styles.fileInput}
              />
              {file && <p style={styles.fileName}>{file.name}</p>}
            </div>

            {error && <p style={styles.err}>{error}</p>}

            <div style={styles.submitBar}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={() => navigate(isCartMode ? '/cart' : '/my-rfqs')}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ ...styles.primaryBtn, backgroundColor: branding.primaryColor }}
                disabled={submitting}
              >
                {submitting ? 'Processing…' : 'Confirm Purchase'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  main: { padding: '24px 24px' },
  container: { maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 },
  noticeBanner: {
    backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #93c5fd',
    borderRadius: 8, padding: '14px 18px', fontSize: 14, lineHeight: 1.5,
  },
  card: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 24,
  },
  sectionTitle: { marginTop: 0, fontSize: 16, fontWeight: 700 },
  muted: { color: 'var(--muted-foreground)', fontSize: 14, marginTop: 4 },
  lineTableWrap: { overflowX: 'auto', marginTop: 12 },
  lineTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  lineTh: {
    textAlign: 'left', padding: '8px 10px', backgroundColor: 'var(--muted)',
    fontWeight: 600, borderBottom: '2px solid var(--border)',
  },
  lineTd: { padding: '8px 10px', borderBottom: '1px solid var(--border)' },
  lineRowEven: { backgroundColor: 'var(--secondary)' },
  field: { marginTop: 16 },
  label: { display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6 },
  required: { color: 'var(--destructive)' },
  optional: { color: 'var(--muted-foreground)', fontWeight: 400, fontSize: 12 },
  input: {
    width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)',
    backgroundColor: 'var(--input-background)', fontSize: 14, boxSizing: 'border-box',
  },
  fileInput: { fontSize: 14 },
  fileName: { color: 'var(--muted-foreground)', fontSize: 13, marginTop: 4 },
  err: { color: 'var(--destructive)', fontSize: 14, marginTop: 12 },
  submitBar: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
  cancelBtn: {
    padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)',
    backgroundColor: 'transparent', fontWeight: 600, cursor: 'pointer', fontSize: 14,
  },
  primaryBtn: {
    color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px',
    fontWeight: 600, cursor: 'pointer', fontSize: 14,
  },
  secondaryBtn: {
    padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)',
    backgroundColor: 'transparent', fontWeight: 600, cursor: 'pointer', fontSize: 14,
  },
  successIcon: {
    width: 56, height: 56, borderRadius: '50%', backgroundColor: '#ecfdf5', color: '#059669',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, fontWeight: 700, margin: '0 auto 16px',
  },
  successTitle: { textAlign: 'center', marginTop: 0 },
  successDetail: { textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 15 },
  successActions: { display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 },
};
